const getters = {
  // UI State getters
  isLoading: (state) => state.isLoading,
  hasError: (state) => state.hasError,
  errorMessage: (state) => state.errorMessage,
  selectedTab: (state) => state.selectedTab,

  // User Settings getters
  userSettings: (state) => state.userSettings,
  lastUpdated: (state) => state.lastUpdated,

  // Profile getters
  profile: (state) => state.userSettings.profile,
  fullName: (state) => {
    const { firstName, lastName } = state.userSettings.profile
    return `${firstName} ${lastName}`.trim() || 'Unknown User'
  },
  userEmail: (state) => state.userSettings.profile.email,
  userTitle: (state) => state.userSettings.profile.title,
  userDepartment: (state) => state.userSettings.profile.department,

  // Preferences getters
  preferences: (state) => state.userSettings.preferences,
  currentTheme: (state) => state.userSettings.preferences.theme,
  isDarkMode: (state) => state.userSettings.preferences.theme === 'dark',
  currentLanguage: (state) => state.userSettings.preferences.language,
  currentTimezone: (state) => state.userSettings.preferences.timezone,
  dateFormat: (state) => state.userSettings.preferences.dateFormat,
  timeFormat: (state) => state.userSettings.preferences.timeFormat,

  // Notifications getters
  notifications: (state) => state.userSettings.notifications,
  emailNotifications: (state) => state.userSettings.notifications.email,
  pushNotifications: (state) => state.userSettings.notifications.push,
  alertSettings: (state) => state.userSettings.notifications.alerts,

  // Check if any notifications are enabled
  hasNotificationsEnabled: (state) => {
    const { email, push, alerts } = state.userSettings.notifications
    return email.enabled || push.enabled || Object.values(alerts).some((alert) => alert)
  },

  // Security getters
  security: (state) => state.userSettings.security,
  hasTwoFactorAuth: (state) => state.userSettings.security.twoFactorAuth,
  sessionTimeout: (state) => state.userSettings.security.sessionTimeout,
  passwordExpiry: (state) => state.userSettings.security.passwordExpiry,

  // Privacy getters
  privacy: (state) => state.userSettings.privacy,
  allowsAnalytics: (state) => state.userSettings.privacy.allowAnalytics,
  sharesUsageData: (state) => state.userSettings.privacy.shareUsageData,
  sharesProfileData: (state) => state.userSettings.privacy.shareProfileData,

  // Demo mode getters
  demoMode: (state) => state.demoMode,
  isDemoModeEnabled: (state) => state.demoMode.enabled,
  usesSampleData: (state) => state.demoMode.sampleData,

  // Alias getters
  aliasSettings: (state) => state.aliasSettings,
  isAliasActive: (state) => state.aliasSettings.active,
  aliasEmail: (state) => state.aliasSettings.email,
  aliasPermissions: (state) => state.aliasSettings.permissions,

  // Filters and reports getters
  savedFilters: (state) => state.savedFilters,
  customReports: (state) => state.customReports,
  hasCustomReports: (state) => state.customReports.length > 0,
  hasSavedFilters: (state) => state.savedFilters.length > 0,

  // Computed settings summary
  settingsSummary: (state) => {
    const { profile, preferences, notifications, security } = state.userSettings
    return {
      user: `${profile.firstName} ${profile.lastName}`.trim() || 'Unknown User',
      theme: preferences.theme,
      notifications: notifications.email.enabled || notifications.push.enabled,
      security: security.twoFactorAuth ? 'Enhanced' : 'Standard',
      lastUpdated: state.lastUpdated,
    }
  },

  // Settings completeness check
  profileCompleteness: (state) => {
    const { profile } = state.userSettings
    const requiredFields = ['firstName', 'lastName', 'email']
    const completedFields = requiredFields.filter((field) => profile[field] && profile[field].trim())
    return Math.round((completedFields.length / requiredFields.length) * 100)
  },

  // Security score
  securityScore: (state) => {
    const { security } = state.userSettings
    let score = 0

    if (security.twoFactorAuth) score += 40
    if (security.sessionTimeout <= 30) score += 20
    if (security.passwordExpiry <= 90) score += 20

    // Additional security checks could be added here
    score += 20 // Base security score

    return Math.min(score, 100)
  },

  // Check if settings need attention
  needsAttention: (state, settingsGetters) => (
    settingsGetters.profileCompleteness < 80 || settingsGetters.securityScore < 60
  ),

  // Export data for settings backup
  exportableSettings: (state) => ({
    preferences: state.userSettings.preferences,
    notifications: state.userSettings.notifications,
    security: {
      sessionTimeout: state.userSettings.security.sessionTimeout,
      passwordExpiry: state.userSettings.security.passwordExpiry,
      // Note: twoFactorAuth should not be exported for security reasons
    },
    privacy: state.userSettings.privacy,
    savedFilters: state.savedFilters,
    customReports: state.customReports,
    exportedAt: new Date().toISOString(),
    version: state.version,
  }),
}

export default getters
